import { useEffect } from "react";

export interface SeoConfig {
  title: string;
  description: string;
  keywords?: string;
  path: string;
  ogType?: string;
  image?: string;
  robots?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const SITE_NAME = "瓜田吃瓜";
const ORIGIN = "https://ftosc.cn";
const DEFAULT_IMAGE =
  "https://readdy.ai/api/search-image?query=Abstract%20stylized%20watermelon%20field%20poster%20art%20with%20bold%20coral%20red%20and%20fresh%20green%20geometric%20shapes%2C%20editorial%20illustration%20style%2C%20soft%20grain%20texture%2C%20warm%20cream%20background%2C%20no%20text%2C%20clean%20modern%20composition&width=1200&height=630&seq=gty-og-image&orientation=landscape";

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function usePageSeo(config: SeoConfig) {
  const {
    title,
    description,
    keywords,
    path,
    ogType = "website",
    image = DEFAULT_IMAGE,
    robots = "index,follow,max-image-preview:large",
    jsonLd,
  } = config;

  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    const url = `${ORIGIN}${path}`;
    document.title = title;

    upsertMeta("name", "description", description);
    if (keywords) {
      upsertMeta("name", "keywords", keywords);
    }
    upsertMeta("name", "robots", robots);
    upsertMeta("name", "last-modified", new Date().toISOString().slice(0, 10));

    upsertMeta("property", "og:type", ogType);
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:locale", "zh_CN");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);

    upsertCanonical(url);

    const existing = document.getElementById("page-jsonld");
    if (existing) {
      existing.remove();
    }
    if (jsonLdKey) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "page-jsonld";
      script.textContent = jsonLdKey;
      document.head.appendChild(script);
    }
  }, [title, description, keywords, path, ogType, image, robots, jsonLdKey]);
}